import React, { useEffect, useState } from "react";
import { Crown, Plus, Trash2, Loader2, Shield, UserCog } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLE_STYLE = {
  admin: "border-orange-200 bg-orange-50 text-orange-700",
  staff: "border-blue-200 bg-blue-50 text-blue-700",
};

export default function AdminTeam() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", role: "staff" });

  const load = async () => { const r = await api.get("/admin/team"); setData(r.data); };
  useEffect(() => { load(); }, []);

  const call = async (fn, msg) => {
    setBusy(true);
    try { await fn(); await load(); if (msg) toast.success(msg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="mt-8 py-20 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-orange-600" /></div>;

  const roleBadge = (role, owner) => (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider rounded-sm border px-1.5 py-0.5 ${ROLE_STYLE[role] || "border-border bg-secondary text-muted-foreground"}`}>
      {owner ? <Crown className="h-3 w-3" /> : role === "admin" ? <Shield className="h-3 w-3" /> : <UserCog className="h-3 w-3" />}
      {owner ? "Owner" : role}
    </span>
  );

  return (
    <div className="mt-8 space-y-8" data-testid="admin-team">
      <div>
        <div className="flex items-center gap-2 mb-2"><UserCog className="h-4 w-4 text-orange-700" /><h2 className="font-heading text-xl text-primary">Team & roles</h2></div>
        <p className="text-sm text-muted-foreground mb-4">Admins can edit the catalog, prices, content and manage requests. Staff can only manage requests. Owners are fixed and always Admin.</p>
        <div className="rounded-xl border border-border bg-card p-4 flex flex-wrap items-end gap-2" data-testid="team-add">
          <div className="flex flex-col gap-1 flex-1 min-w-[240px]">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Email</span>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="person@email.com" className="h-9 bg-background border-border" data-testid="team-email" />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Role</span>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="h-9 w-36 bg-background border-border" data-testid="team-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button disabled={busy || !form.email} onClick={() => call(() => api.post("/admin/team", { email: form.email, role: form.role }).then(() => setForm({ email: "", role: "staff" })), "Role assigned")} className="h-9 rounded-sm bg-orange-500 text-white hover:bg-orange-600" data-testid="team-add-btn"><Plus className="h-4 w-4 mr-1" /> Assign</Button>
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Owners</p>
        <div className="rounded-xl border border-border bg-card divide-y divide-border">
          {data.owners.map((o) => (
            <div key={o.email} className="flex items-center gap-3 px-4 py-3 text-sm" data-testid={`team-owner-${o.email}`}>
              <span className="text-foreground">{o.email}</span>
              {roleBadge("admin", true)}
              <span className="ml-auto text-xs text-muted-foreground">Locked</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Assigned members</p>
        <div className="rounded-xl border border-border bg-card divide-y divide-border">
          {data.members.length === 0 && <p className="p-4 text-sm text-muted-foreground">No members assigned yet. Add one above.</p>}
          {data.members.map((m) => (
            <div key={m.email} className="flex items-center gap-3 px-4 py-3 text-sm" data-testid={`team-member-${m.email}`}>
              <span className="text-foreground">{m.email}</span>
              {roleBadge(m.role, false)}
              <div className="ml-auto flex items-center gap-2">
                <Select value={m.role} onValueChange={(v) => call(() => api.post("/admin/team", { email: m.email, role: v }), "Role updated")}>
                  <SelectTrigger className="h-8 w-28 bg-background border-border text-xs" data-testid={`team-member-role-${m.email}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => call(() => api.delete(`/admin/team/${encodeURIComponent(m.email)}`), "Removed")} className="h-8 rounded-sm border-red-200 text-red-600 bg-card hover:bg-red-50" data-testid={`team-remove-${m.email}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
